import type { SageWriteAuditEntry } from '../../services/portalOrders';

/**
 * Human-readable summary of one pending (unconfirmed) cloud->Sage write, for
 * the OrderDetailModal "pushed to Sage — awaiting confirmation" banner.
 *
 * - ship_to_create → "New ship-to <code> created in Sage"
 * - ship_to_update → "Ship-to <code> updated in Sage: <field>: <old> → <new>"
 * - order_update   → "<field>: <old> → <new>; …" from the recorded changes
 *   (so the CSR sees current→pushed until the reconciler confirms).
 */
export function formatPendingSageWrite(w: SageWriteAuditEntry): string {
  if (w.kind === 'ship_to_create') {
    return w.entity_ref
      ? `New ship-to ${w.entity_ref} created in Sage`
      : 'New ship-to created in Sage';
  }
  if (w.kind === 'ship_to_update') {
    // Edit-in-place of a portal-created code (kanban 6a7b740b): the audit
    // row's field changes say what moved; the edit_of / proposal entries are
    // provenance, not a diff.
    const fields = (w.changes ?? []).filter((c) => c.kind === 'field');
    const head = w.entity_ref
      ? `Ship-to ${w.entity_ref} updated in Sage`
      : 'Ship-to updated in Sage';
    if (fields.length === 0) return head;
    return `${head}: ${fields
      .map((c) => `${c.field}: ${formatChangeValue(c.old)} → ${formatChangeValue(c.new)}`)
      .join('; ')}`;
  }
  // kind="proposal" entries are Merlin-proposal provenance for the audit
  // trail (a dict payload), not an old→new diff — never render them raw
  // ("merlin_proposal: (blank) → [object Object]"). A Merlin-confirmed write
  // is labeled instead.
  const all = w.changes ?? [];
  const changes = all.filter((c) => c.kind !== 'proposal');
  const viaMerlin = w.source === 'merlin_proposed' ? ' (Merlin proposal, confirmed)' : '';
  if (changes.length === 0) return `Order update pushed${viaMerlin}`;
  return (
    changes
      .map((c) => {
        const settled = formatSettledLinePrice(c);
        if (settled !== null) return `${c.field}: ${settled}`;
        return `${c.field}: ${formatChangeValue(c.old)} → ${formatChangeValue(c.new)}`;
      })
      .join('; ') + viaMerlin
  );
}

const PRICE_SOURCE_LABELS: Record<string, string> = {
  stated: 'stated',
  no_charge: 'no charge',
  no_price_confirmed: 'no price, confirmed',
  product_hybrid: 'catalog price',
  special_pricing: 'special pricing',
  special_pricing_base_discount: 'base discount',
  customer_quote: 'customer quote',
};

/**
 * The line-add pricing audit entries (kanban dc8caa8d) carry a dict payload
 * `{unit_price, source, quote_id?}` rather than an old→new pair:
 *   line_add_priced    → "priced $170.00 (special pricing)"
 *   line_add_no_charge → "sent at $0.00 (no price, confirmed)"
 * Returns null for every other change kind.
 */
export function formatSettledLinePrice(c: {
  kind?: string;
  new?: unknown;
}): string | null {
  if (c.kind !== 'line_add_priced' && c.kind !== 'line_add_no_charge') return null;
  const payload = (c.new ?? {}) as { unit_price?: unknown; source?: unknown };
  const price = Number(payload.unit_price);
  const source = typeof payload.source === 'string' ? payload.source : '';
  const label = PRICE_SOURCE_LABELS[source] ?? source;
  const money = Number.isFinite(price) ? `$${price.toFixed(2)}` : '(no price)';
  const verb = c.kind === 'line_add_priced' ? 'priced' : 'sent at';
  return label ? `${verb} ${money} (${label})` : `${verb} ${money}`;
}

/** One side of an old→new diff as text — never "[object Object]". */
export function formatChangeValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '(blank)';
  if (typeof v === 'object') {
    const o = v as { unit_price?: unknown; source?: unknown };
    if (o.unit_price !== undefined) {
      const price = Number(o.unit_price);
      const src = typeof o.source === 'string' ? PRICE_SOURCE_LABELS[o.source] ?? o.source : '';
      return `${Number.isFinite(price) ? `$${price.toFixed(2)}` : String(o.unit_price)}${src ? ` (${src})` : ''}`;
    }
    return JSON.stringify(v);
  }
  return String(v);
}
